import { forkJoin, Observable, of } from "rxjs";
import { catchError, switchMap, take, tap } from "rxjs/operators";
import { MultiSelectChipOption } from "src/app/components/multi-select-chips/multi-select-chips.component";
import { InspectionTypeSummary } from "src/app/models/inspection-type-summary.model";
import { LanguageDictionary } from "src/app/models/language-dictionary.model";
import { MirRuleHttp } from "src/app/models/mir-rule.model";
import { GlobalApiCallsService } from "src/app/services/global-api-calls.service";
import { LanguageDictionaryService } from "src/app/services/language-dictionary/language-dictionary.service";
import { RulesService } from "src/app/services/rules/rules.service";
import { environment } from "src/environments/environment";
import { WizardId, WizardService } from "../../services/wizard/wizard.service";
import { makeKey, TripFrequencyUi, TripSemantics } from "./define-duty/define-duty.component";
import { StepProps } from "./step-props";

/** Structs to keep the pipe tidy */
type BootstrapData = {
	config: any[];
	inspectionTypes: InspectionTypeSummary[];
};

type InitArgs = {
	wizardId: WizardId;
	data: MirRuleHttp | undefined;
	companyId$: Observable<string>;
	languageDictionary$?: Observable<LanguageDictionary>;
	languageDictionaryService: LanguageDictionaryService;
	rulesService: RulesService;
};

export function init(
	wizardService: WizardService,
	globalApiCallsService: GlobalApiCallsService,
	args: InitArgs,
): Observable<void> {
	wizardService.clear(WizardId.createRule);

	setTitleIfEditing(wizardService, args);
	setInitialData(wizardService, args.wizardId, args.data);

	console.log("### wizard init >>> data", args.data);

	return args.companyId$.pipe(
		switchMap(companyId => {
			setCompanyId(wizardService, args.wizardId, companyId);

			// collect selections from incoming data up-front
			const tripStartSelectedInspectionIds = deriveSelectedInspectionIds(TripSemantics.tripStart, args.data);
			const tripEndSelectedInspectionIds = deriveSelectedInspectionIds(TripSemantics.tripEnd, args.data);

			// fetch base data
			return fetchBootstrapData(globalApiCallsService, wizardService, companyId).pipe(
				// apply config/zones and prepopulate fields not dependent on language
				tap(({ config }) => {
					applyConfig(wizardService, args.wizardId, config);
					prepopulateImmediateFields(wizardService, args.wizardId, args.data);
				}),

				// build inspection-type chips after language is ready, with preselection
				switchMap(({ inspectionTypes }) =>
					waitForLanguage(args.languageDictionary$).pipe(
						tap(() => {
							const translate = getTranslate(args.languageDictionaryService);

							const tripStartChips = buildInspectionTypeChips(
								inspectionTypes,
								translate,
								tripStartSelectedInspectionIds,
							);
							const tripEndChips = buildInspectionTypeChips(
								inspectionTypes,
								translate,
								tripEndSelectedInspectionIds,
							);

							// stored where DefineDutyComponent.restore() already looks (fallback path)
							setInspectionTypeChips(
								wizardService,
								args.wizardId,
								tripStartChips,
								TripSemantics.tripStart,
							);
							setInspectionTypeChips(wizardService, args.wizardId, tripEndChips, TripSemantics.tripEnd);
						}),
					),
				),
			);
		}),
		catchError(err => {
			console.error("Unexpected error in init:", err);
			return of(undefined);
		}),
	);
}

/* -------------------- prepopulation helpers -------------------- */

function prepopulateImmediateFields(wizardService: WizardService, wizardId: WizardId, data?: MirRuleHttp): void {
	if (!data) return;
	setRuleMeta(wizardService, wizardId, data);
	setTimezonesIfAny(wizardService, wizardId, data);
	prepopulateTripSide(wizardService, wizardId, data.tripStart, TripSemantics.tripStart);
	prepopulateTripSide(wizardService, wizardId, (data as any).tripEnd, TripSemantics.tripEnd);
}

function setRuleMeta(wizardService: WizardService, wizardId: WizardId, data: MirRuleHttp) {
	wizardService.setValue(wizardId, StepProps.Common_Original_RuleName, data.name);
	wizardService.setValue(wizardId, StepProps.ReviewAndSubmit_RuleName, data.name);
	wizardService.setValue(wizardId, StepProps.ReviewAndSubmit_RuleId, data.id);

	// excludedZones
	const excludedZones = data.excludeZones;
	if (Array.isArray(excludedZones)) {
		wizardService.setValue(wizardId, StepProps.ReviewAndSubmit_ExcludedZones, excludedZones);
	}
}

function setTimezonesIfAny(wizardService: WizardService, wizardId: WizardId, data: MirRuleHttp) {
	if ((data[TripSemantics.tripStart] as any)?.timezone) {
		wizardService.setValue(
			wizardId,
			makeKey(StepProps.DefineDuty_Timezone, TripSemantics.tripStart),
			(data[TripSemantics.tripStart] as any)?.timezone,
		);
	}
	if ((data[TripSemantics.tripEnd] as any)?.timezone) {
		wizardService.setValue(
			wizardId,
			makeKey(StepProps.DefineDuty_Timezone, TripSemantics.tripEnd),
			(data[TripSemantics.tripEnd] as any)?.timezone,
		);
	}
}

function prepopulateTripSide(
	wizardService: WizardService,
	wizardId: WizardId,
	tripSide: any | null | undefined,
	semantics: TripSemantics,
) {
	if (!tripSide) return;

	setStartConditionIfAny(wizardService, wizardId, tripSide, semantics);
	setFrequencyIfAny(wizardService, wizardId, tripSide, semantics);
	setExpireMinutesIfAny(wizardService, wizardId, tripSide, semantics);
	setWorkPeriodsIfAny(wizardService, wizardId, tripSide, semantics);
	setZonesIfAny(wizardService, wizardId, tripSide, semantics);
}

/** tiny setters for each field **/

function setStartConditionIfAny(wizardService: WizardService, wizardId: WizardId, ts: any, semantics: TripSemantics) {
	if (!ts?.definition) return;
	let startCondition: string | null = null;
	if (ts.definition === "power_on" || ts.definition === "power_off") startCondition = "power";
	else if (ts.definition === "exit_zone" || ts.definition === "enter_zone") startCondition = "selected-zone";
	else startCondition = "after_distance";

	wizardService.setValue(wizardId, makeKey(StepProps.DefineDuty_StartCondition, semantics), startCondition);
}

function setFrequencyIfAny(wizardService: WizardService, wizardId: WizardId, ts: any, semantics: TripSemantics) {
	// canonicalize to what the UI expects
	let frequency: TripFrequencyUi = TripFrequencyUi.trip;
	if (ts?.frequency) {
		if (ts.frequency === "trip") {
			if (Array.isArray(ts?.workPeriods)) {
				frequency = TripFrequencyUi.tripWithinWorkperiod;
			}
		} else {
			frequency = TripFrequencyUi.workPeriod;
		}
	}
	wizardService.setValue(wizardId, makeKey(StepProps.DefineDuty_Frequency, semantics), frequency);
}

function setExpireMinutesIfAny(wizardService: WizardService, wizardId: WizardId, ts: any, semantics: TripSemantics) {
	if (typeof ts?.within !== "number") return;
	wizardService.setValue(wizardId, makeKey(StepProps.DefineDuty_ExpireMinutes, semantics), ts.within);
}

function setWorkPeriodsIfAny(wizardService: WizardService, wizardId: WizardId, ts: any, semantics: TripSemantics) {
	if (!Array.isArray(ts?.workPeriods)) return;
	const normalized = normalizeWorkPeriods(ts.workPeriods);
	wizardService.setValue(wizardId, makeKey(StepProps.DefineDuty_WorkPeriods, semantics), normalized);
}

function setZonesIfAny(wizardService: WizardService, wizardId: WizardId, ts: any, semantics: TripSemantics) {
	if (!Array.isArray(ts?.zones)) return;
	wizardService.setValue(wizardId, makeKey(StepProps.DefineDuty_Zones, semantics), ts.zones);
}

/* -------------------- derived selections -------------------- */

function deriveSelectedZoneIds(data?: MirRuleHttp): string[] {
	const ids = (data as any)?.tripStart?.zones;
	return Array.isArray(ids) ? ids : [];
}

/**
 * normalization:
 * Accepts common shapes and always returns string IDs.
 */
function deriveSelectedInspectionIds(semantics: TripSemantics, data?: MirRuleHttp): string[] {
	const items = (data as any)?.[semantics]?.inspectionTypes;
	if (!Array.isArray(items)) return [];
	return items
		.map((it: any) => (typeof it?.inspectionType === "string" ? it.inspectionType : null))
		.filter((x: any): x is string => typeof x === "string" && x.length > 0);
}

/* -------------------- fetch & base-apply helpers -------------------- */

function setTitleIfEditing(wizardService: WizardService, args: InitArgs): void {
	if (args.data) {
		wizardService.setWizardTitle(`Edit Rule: ${args.data.name}`, WizardId.createRule);
	}
}

function setCompanyId(wizardService: WizardService, wizardId: WizardId, companyId: string): void {
	wizardService.setValue(wizardId, StepProps.Common_CompanyId, companyId);
}

function fetchBootstrapData(
	api: GlobalApiCallsService,
	wizardService: WizardService,
	companyId: string,
): Observable<BootstrapData> {
	const EVIR_V2 = `${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}`;
	const CORE = `${environment.environmentConstants.APP_ENDPOINT_CORE}`;

	return forkJoin({
		config: api.getConfig(companyId, EVIR_V2).pipe(
			catchError(err => {
				console.error("Error fetching config:", err);
				wizardService.setHasError(WizardId.createRule);
				return of<any[]>([]);
			}),
		),
		inspectionTypes: api.getInspectionTypes(companyId, EVIR_V2).pipe(
			catchError(err => {
				console.error("Error fetching inspection types:", err);
				wizardService.setHasError(WizardId.createRule);
				return of<InspectionTypeSummary[]>([]);
			}),
		),
	});
}

function applyConfig(wizardService: WizardService, wizardId: WizardId, config: any[]): void {
	wizardService.setValue(wizardId, StepProps.Common_Config, config?.[0]);
}

function applyZones(wizardService: WizardService, wizardId: WizardId, zones: any[], selectedZoneIds: string[]): void {
	const zoneOptions: Partial<MultiSelectChipOption>[] = (zones ?? []).map(zone => ({
		label: zone.name,
		value: zone.id,
		selected: selectedZoneIds.includes(zone.id),
		categoryId: zone.categoryId,
	}));
	wizardService.setValue(wizardId, StepProps.Common_Zones, zoneOptions);
}

/* -------------------- language + inspection chips -------------------- */

function waitForLanguage(languageDictionary$?: Observable<LanguageDictionary>) {
	return languageDictionary$ ? languageDictionary$.pipe(take(1)) : of(null);
}

function getTranslate(languageDictionaryService: LanguageDictionaryService) {
	return languageDictionaryService.getTranslation.bind(languageDictionaryService) as (k: string) => string;
}

/**
 * Builds chips from /inspection-types response: `[{inspectionTypeId, name, langId}, ...]`.
 * chip.value = [inspectionTypeId, langId]; label translates via langId with fallback to name.
 */
function buildInspectionTypeChips(
	inspectionTypes: InspectionTypeSummary[] | undefined,
	translate: (k: string) => string,
	selectedInspectionIds: string[],
): MultiSelectChipOption[] {
	const chips: MultiSelectChipOption[] = (inspectionTypes ?? []).map(({ inspectionTypeId, name, langId }) => {
		const selected = selectedInspectionIds.includes(inspectionTypeId);

		return {
			label: translate(langId) || name,
			// value: [inspectionTypeId, langId]
			value: [inspectionTypeId, langId],
			selected,
		};
	});

	chips.sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" }));
	return chips;
}

function setInspectionTypeChips(
	wizardService: WizardService,
	wizardId: WizardId,
	chips: MultiSelectChipOption[],
	semantics: TripSemantics,
): void {
	wizardService.setValue(wizardId, makeKey(StepProps.DefineDuty_InspectionTypes, semantics), chips);
}

function setInitialData(wizardService: WizardService, wizardId: WizardId, rule: MirRuleHttp): void {
	wizardService.setValue(wizardId, StepProps.Common_Original_Data, rule);
}

/* -------------------- helpers: work period normalization -------------------- */

/**
 * Supports:
 * - days as indices (0..6 where 0=Su, 6=Sat)  — matches your sample [6]
 * - days as names/abbrevs, or object of booleans (long/short keys)
 * - start/end as strings ("HH:mm" or "hh:mm AM/PM") OR objects {hr,min} — matches your sample
 */
function normalizeWorkPeriods(apiWps: any[]): any[] {
	type DaysObj = { M: boolean; T: boolean; W: boolean; Th: boolean; F: boolean; S: boolean; Su: boolean };

	const idxToKey = (i: number): keyof DaysObj | undefined =>
		i === 0
			? "Su"
			: i === 1
			? "M"
			: i === 2
			? "T"
			: i === 3
			? "W"
			: i === 4
			? "Th"
			: i === 5
			? "F"
			: i === 6
			? "S"
			: undefined;

	const dayKey = (d: any): keyof DaysObj | undefined => {
		if (typeof d === "number") return idxToKey(d);
		const s = String(d || "").toLowerCase();
		if (s.startsWith("su")) return "Su";
		if (s === "m" || s.startsWith("mo")) return "M";
		if (s === "t" || s.startsWith("tu")) return "T";
		if (s.startsWith("w")) return "W";
		if (s.startsWith("th")) return "Th";
		if (s.startsWith("f")) return "F";
		if (s.startsWith("sa")) return "S";
		return undefined;
	};

	const to12hFromObj = (o: any): string => {
		if (!o || typeof o.hr !== "number" || typeof o.min !== "number") return "00:00 AM";
		const hr24 = o.hr;
		const mm = String(o.min).padStart(2, "0");
		const ampm = hr24 >= 12 ? "PM" : "AM";
		let hh = hr24 % 12;
		if (hh === 0) hh = 12;
		return `${String(hh).padStart(2, "0")}:${mm} ${ampm}`;
	};

	const to12hFromStr = (t: any): string => {
		if (typeof t !== "string" || !t) return "00:00 AM";
		if (/[AP]M$/i.test(t)) return t.toUpperCase();
		const m = /^(\d{1,2}):(\d{2})$/.exec(t);
		if (!m) return t; // unknown format, pass through
		const hr24 = parseInt(m[1], 10);
		const mm = m[2];
		const ampm = hr24 >= 12 ? "PM" : "AM";
		let hh = hr24 % 12;
		if (hh === 0) hh = 12;
		return `${String(hh).padStart(2, "0")}:${mm} ${ampm}`;
	};

	return (apiWps ?? []).map((wp: any) => {
		const daysObj: DaysObj = { M: false, T: false, W: false, Th: false, F: false, S: false, Su: false };

		if (Array.isArray(wp?.days)) {
			wp.days.forEach((d: any) => {
				const k = dayKey(d);
				if (k) (daysObj as any)[k] = true;
			});
		} else if (typeof wp?.days === "object" && wp.days) {
			Object.entries(wp.days).forEach(([k, v]) => {
				const mapped = (["M", "T", "W", "Th", "F", "S", "Su"] as const).includes(k as any)
					? (k as keyof DaysObj)
					: dayKey(k);
				if (mapped) (daysObj as any)[mapped] = !!v;
			});
		} else if (Array.isArray(wp?.weekdays)) {
			wp.weekdays.forEach((d: any) => {
				const k = dayKey(d);
				if (k) (daysObj as any)[k] = true;
			});
		}

		const start = wp?.start && typeof wp.start === "object" ? to12hFromObj(wp.start) : to12hFromStr(wp?.start);

		const end = wp?.end && typeof wp.end === "object" ? to12hFromObj(wp.end) : to12hFromStr(wp?.end);

		const allWeek = Object.values(daysObj).every(Boolean);

		return {
			allWeek,
			days: daysObj,
			start,
			end,
		};
	});
}
